import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { api } from "../../../api/apiClient";
import PeopleIcon from "@mui/icons-material/People";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { notify } from "../../components-ui/toast";
import PageHeader from "../../../app/layout/PageHeader";
import RegisterPartyToInstanceTab from "./RegisterPartyToInstanceTab";
import ServiceInstanceAttendeesTab from "./ServiceInstanceAttendeesTab";
import { Party, SelectedSession } from "../../types/types";
import "../../../css/buttons.css";
import "../../../css/forms.css";
import "./RegistrationPage.css";

interface ServiceDto {
  serviceId: number;
  name: string;
}

interface ServiceInstanceDto {
  serviceInstanceId: number;
  startDateTime: string;
  endDateTime: string;
}

interface LocationState {
  session?: SelectedSession;
  partyId?: number;
  selectedServiceId?: number;
  registrationOrigin?: "dashboard" | "services";
  originServiceId?: number;
}

export default function C() {
  const history = useHistory();
  const location = useLocation<LocationState>();
  const [services, setServices] = useState<ServiceDto[]>([]);
  const [instances, setInstances] = useState<ServiceInstanceDto[]>([]);
  const [selectedServiceId, setSelectedServiceId] = useState<number | null>(
    location.state?.selectedServiceId ??
      location.state?.session?.serviceId ??
      null,
  );
  const [selectedSession, setSelectedSession] =
    useState<SelectedSession | null>(location.state?.session ?? null);
  const [returnedPartyId, setReturnedPartyId] = useState<number | null>(
    location.state?.partyId ?? null,
  );
  const [showAttendees, setShowAttendees] = useState(false);
  const [loading, setLoading] = useState(false);
  const isSessionSelected = selectedSession !== null;
  const [attendeeCount, setAttendeeCount] = useState<number>(0);
  const [selectedParty, setSelectedParty] = useState<Party | null>(null);
  const registrationOrigin = location.state?.registrationOrigin ?? "dashboard";
  const originServiceId = location.state?.originServiceId;

  const handleBack = () => {
    if (registrationOrigin === "services") {
      history.push(
        originServiceId
          ? `/admin/manage-services?serviceId=${originServiceId}`
          : "/admin/manage-services",
      );
      return;
    }

    history.push("/dashboard");
  };

  useEffect(() => {
    if (location.state?.partyId) {
      setReturnedPartyId(location.state.partyId);
    }
  }, [location.state]);

  /* =========================
     Load services
     ========================= */

  useEffect(() => {
    api<ServiceDto[]>("/api/services")
      .then(setServices)
      .catch(() => notify.error("Failed to load services"));
  }, []);

  /* =========================
     Load sessions when service selected
     ========================= */

  useEffect(() => {
    if (!selectedServiceId) {
      setInstances([]);
      return;
    }

    setLoading(true);

    api<ServiceInstanceDto[]>(
      `/api/serviceinstances/search-by-service?serviceId=${selectedServiceId}`,
    )
      .then(setInstances)
      .catch(() => notify.error("Failed to load sessions"))
      .finally(() => setLoading(false));
  }, [selectedServiceId]);

  useEffect(() => {
    if (selectedSession?.serviceInstanceId) {
      loadAttendeeCount(selectedSession.serviceInstanceId);
    }
  }, [selectedSession]);

  const loadAttendeeCount = async (instanceId: number) => {
    try {
      const count = await api<number>(
        `/api/serviceinstances/${instanceId}/registrations/count`,
      );

      setAttendeeCount(count);
    } catch {
      notify.error("Failed to load attendee count");
    }
  };

  const handleRegistered = (count: number) => {
    setAttendeeCount(count);
  };
  /* =========================
     Format selected session
     ========================= */

  const start = selectedSession
    ? new Date(selectedSession.startDateTime)
    : null;

  const end = selectedSession ? new Date(selectedSession.endDateTime) : null;

  const formattedDate = start
    ? start.toLocaleDateString(undefined, {
        weekday: "short",
        day: "numeric",
        month: "short",
      })
    : "";

  const formattedStart = start
    ? start.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : "";

  const formattedEnd = end
    ? end.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : "";

  return (
    <div className="page-container">
      <PageHeader
        title="Registrations"
        subtitle="Check people into sessions and review session attendees."
        actions={
          <button
            type="button"
            className="icon-button-muted-solid"
            onClick={handleBack}
            title={
              registrationOrigin === "services"
                ? "Back to service management"
                : "Back to dashboard"
            }
            aria-label={
              registrationOrigin === "services"
                ? "Back to service management"
                : "Back to dashboard"
            }
          >
            <ArrowBackIcon fontSize="small" />
          </button>
        }
      />

      {/* SESSION FILTER */}

      <div className="filter-header registration-session-filter">
        {isSessionSelected ? (
          <div className="summary summary-inline">
            <div className="summary-inline-text">
              <strong>{selectedSession.serviceName}</strong>

              {start && (
                <div className="summary-inline-sub">
                  {formattedDate} • {formattedStart}
                  {formattedEnd && ` – ${formattedEnd}`}
                </div>
              )}
            </div>

            <div className="registration-session-actions">
              <button
                type="button"
                className="registration-attendees-action"
                onClick={() => setShowAttendees(true)}
                aria-current={showAttendees ? "page" : undefined}
              >
                <PeopleIcon fontSize="small" />
                <span>View attendees ({attendeeCount})</span>
              </button>
              <button
                type="button"
                className="icon-button-solid icon-button-solid--danger"
                onClick={() => {
                  setSelectedSession(null);
                  setSelectedParty(null);
                  setShowAttendees(false);
                }}
                title="Change session"
                aria-label="Change session"
              >
                <CloseIcon fontSize="small" />
              </button>
            </div>
          </div>
        ) : (
          <div className="registration-session-required">
            <div className="form-row registration-session-filter-row">
            <div
              className={`form-group registration-session-filter-field ${
                !selectedServiceId
                  ? "registration-session-filter-field--invalid"
                  : ""
              }`}
            >
              <select
                className="form-input registration-session-select"
                aria-label="Select service"
                value={selectedServiceId ?? ""}
                onChange={(e) =>
                  setSelectedServiceId(
                    e.target.value ? Number(e.target.value) : null,
                  )
                }
              >
                <option value="">Select service</option>

                {services.map((s) => (
                  <option key={s.serviceId} value={s.serviceId}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>

            <div
              className={`form-group registration-session-filter-field ${
                !selectedSession
                  ? "registration-session-filter-field--invalid"
                  : ""
              }`}
            >
              <select
                className="form-input registration-session-select"
                aria-label="Select session"
                disabled={!selectedServiceId}
                onChange={(e) => {
                  const instance = instances.find(
                    (i) => i.serviceInstanceId === Number(e.target.value),
                  );

                  const service = services.find(
                    (s) => s.serviceId === selectedServiceId,
                  );

                  if (!instance || !service) return;

                  setSelectedSession({
                    serviceId: service.serviceId,
                    serviceName: service.name,
                    serviceInstanceId: instance.serviceInstanceId,
                    startDateTime: instance.startDateTime,
                    endDateTime: instance.endDateTime,
                  });
                }}
              >
                <option value="">
                  {selectedServiceId
                    ? "Select a session"
                    : "Select a service first"}
                </option>

                {instances.map((si) => (
                  <option
                    key={si.serviceInstanceId}
                    value={si.serviceInstanceId}
                  >
                    {new Date(si.startDateTime).toLocaleString()}
                  </option>
                ))}
              </select>
            </div>
            </div>
            <div className="registration-session-message" role="status">
              Select a service and session to continue.
            </div>
          </div>
        )}
      </div>

      {/* CHECK-IN WORKSPACE */}

      {isSessionSelected && (
        <>
          {!showAttendees && (
            <RegisterPartyToInstanceTab
              session={selectedSession}
              selectedParty={selectedParty}
              onSelectedPartyChange={setSelectedParty}
              returnedPartyId={returnedPartyId}
              loading={loading}
              onRegistered={handleRegistered}
              onAddPerson={() =>
                history.push("/manage-party", {
                  returnTo: "registration",
                  session: selectedSession,
                  registrationOrigin,
                  originServiceId,
                })
              }
              onEditPerson={(partyId) =>
                history.push("/manage-party", {
                  returnTo: "registration",
                  session: selectedSession,
                  partyId,
                  registrationOrigin,
                  originServiceId,
                })
              }
            />
          )}

          {showAttendees && selectedSession && (
            <section className="registration-attendees-view">
              <button
                type="button"
                className="link-button link-button-with-icon registration-back-to-check-in"
                onClick={() => setShowAttendees(false)}
              >
                <ArrowBackIcon fontSize="small" />
                Back to check in
              </button>
              <ServiceInstanceAttendeesTab
                serviceInstanceId={selectedSession.serviceInstanceId}
              />
            </section>
          )}
        </>
      )}
    </div>
  );
}
